import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-kanban',
  version: '1.0.0',
  properties: [
    {
      key: 'environment',
      label: 'Environment',
      type: 'options',
      required: true,
      default: 'preprod',
      options: [
        { value: 'preprod', name: 'Preprod' },
        { value: 'production', name: 'Production' },
      ],
    },
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'task',
      options: [
        { value: 'board', name: 'Board' },
        { value: 'boardMember', name: 'Board Member' },
        { value: 'column', name: 'Column' },
        { value: 'task', name: 'Task' },
        { value: 'taskType', name: 'Task Type' },
        { value: 'comment', name: 'Comment' },
        { value: 'attachment', name: 'Attachment' },
        { value: 'stakeholder', name: 'Stakeholder' },
        { value: 'stakeholderType', name: 'Stakeholder Type' },
        { value: 'tag', name: 'Tag' },
        { value: 'team', name: 'Team' },
      ],
    },
    // Board actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getBoards',
      options: [
        { value: 'getBoards', name: 'Get All Boards' },
        { value: 'getBoard', name: 'Get Board by ID' },
        { value: 'createBoard', name: 'Create Board' },
        { value: 'updateBoard', name: 'Update Board' },
      ],
      displayOptions: {
        show: {
          resource: ['board'],
        },
      },
    },
    // Board Member actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getBoardMembers',
      options: [
        { value: 'getBoardMembers', name: 'Get Board Members' },
        { value: 'addBoardMember', name: 'Add Member to Board' },
        { value: 'updateBoardMember', name: 'Update Member Role' },
        { value: 'removeBoardMember', name: 'Remove Member' },
      ],
      displayOptions: {
        show: {
          resource: ['boardMember'],
        },
      },
    },
    // Column actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getColumns',
      options: [
        { value: 'getColumns', name: 'Get Columns' },
        { value: 'createColumn', name: 'Create Column' },
        { value: 'updateColumn', name: 'Update Column' },
        { value: 'deleteColumn', name: 'Delete Column' },
        { value: 'reorderColumn', name: 'Reorder Column' },
      ],
      displayOptions: {
        show: {
          resource: ['column'],
        },
      },
    },
    // Task actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTasks',
      options: [
        { value: 'getTasks', name: 'Get Tasks' },
        { value: 'getTask', name: 'Get Task by ID' },
        { value: 'createTask', name: 'Create Task' },
        { value: 'updateTask', name: 'Update Task' },
        { value: 'moveTask', name: 'Move Task' },
        { value: 'removeFromColumn', name: 'Remove from Column' },
        { value: 'removeFromBoard', name: 'Remove from Board' },
        { value: 'assignToBoard', name: 'Assign to Board' },
        { value: 'deleteTask', name: 'Delete Task' },
      ],
      displayOptions: {
        show: {
          resource: ['task'],
        },
      },
    },
    // Task Type actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTaskTypes',
      options: [
        { value: 'getTaskTypes', name: 'Get Task Types' },
        { value: 'getTaskType', name: 'Get Task Type by ID' },
        { value: 'createTaskType', name: 'Create Task Type' },
        { value: 'updateTaskType', name: 'Update Task Type' },
        { value: 'deleteTaskType', name: 'Delete Task Type' },
      ],
      displayOptions: {
        show: {
          resource: ['taskType'],
        },
      },
    },
    // Comment actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getComments',
      options: [
        { value: 'getComments', name: 'Get Comments' },
        { value: 'addComment', name: 'Add Comment' },
        { value: 'updateComment', name: 'Update Comment' },
        { value: 'deleteComment', name: 'Delete Comment' },
      ],
      displayOptions: {
        show: {
          resource: ['comment'],
        },
      },
    },
    // Attachment actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getAttachments',
      options: [
        { value: 'getAttachments', name: 'Get Attachments' },
      ],
      displayOptions: {
        show: {
          resource: ['attachment'],
        },
      },
    },
    // Stakeholder actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getStakeholders',
      options: [
        { value: 'getStakeholders', name: 'Get Stakeholders' },
        { value: 'getStakeholder', name: 'Get Stakeholder by ID' },
        { value: 'createStakeholder', name: 'Create Stakeholder' },
        { value: 'updateStakeholder', name: 'Update Stakeholder' },
        { value: 'deleteStakeholder', name: 'Delete Stakeholder' },
      ],
      displayOptions: {
        show: {
          resource: ['stakeholder'],
        },
      },
    },
    // Stakeholder Type actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getStakeholderTypes',
      options: [
        { value: 'getStakeholderTypes', name: 'Get Stakeholder Types' },
        { value: 'getStakeholderType', name: 'Get Type by ID' },
        { value: 'createStakeholderType', name: 'Create Stakeholder Type' },
        { value: 'updateStakeholderType', name: 'Update Stakeholder Type' },
        { value: 'deleteStakeholderType', name: 'Delete Stakeholder Type' },
      ],
      displayOptions: {
        show: {
          resource: ['stakeholderType'],
        },
      },
    },
    // Tag actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTags',
      options: [
        { value: 'getTags', name: 'Get Tags' },
        { value: 'getTag', name: 'Get Tag by ID' },
        { value: 'createTag', name: 'Create Tag' },
        { value: 'updateTag', name: 'Update Tag' },
        { value: 'deleteTag', name: 'Delete Tag' },
      ],
      displayOptions: {
        show: {
          resource: ['tag'],
        },
      },
    },
    // Team actions
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTeams',
      options: [
        { value: 'getTeams', name: 'Get Teams' },
        { value: 'getTeam', name: 'Get Team by ID' },
        { value: 'createTeam', name: 'Create Team' },
        { value: 'updateTeam', name: 'Update Team' },
        { value: 'deleteTeam', name: 'Delete Team' },
      ],
      displayOptions: {
        show: {
          resource: ['team'],
        },
      },
    },
    // Board ID field (required for board-specific actions)
    {
      key: 'boardId',
      label: 'Board ID',
      type: 'string',
      required: true,
      placeholder: 'board-uuid or {{previousNode.boardId}}',
      description: 'The board ID (supports variable interpolation)',
      displayOptions: {
        show: {
          action: [
            'getBoard',
            'updateBoard',
            'getBoardMembers',
            'addBoardMember',
            'updateBoardMember',
            'removeBoardMember',
            'getColumns',
            'createColumn',
            'updateColumn',
            'deleteColumn',
            'reorderColumn',
            'getStakeholders',
            'getStakeholder',
            'createStakeholder',
            'updateStakeholder',
            'deleteStakeholder',
            'getStakeholderTypes',
            'getStakeholderType',
            'createStakeholderType',
            'updateStakeholderType',
            'deleteStakeholderType',
            'getTaskTypes',
            'getTaskType',
            'createTaskType',
            'updateTaskType',
            'deleteTaskType',
            'getTags',
            'getTag',
            'createTag',
            'updateTag',
            'deleteTag',
            'getAttachments',
          ],
        },
      },
    },
    // Member ID field
    {
      key: 'memberId',
      label: 'Member ID',
      type: 'string',
      required: true,
      placeholder: 'member-uuid',
      description: 'The member ID',
      displayOptions: {
        show: {
          action: ['updateBoardMember', 'removeBoardMember'],
        },
      },
    },
    // Stakeholder ID field
    {
      key: 'stakeholderId',
      label: 'Stakeholder ID',
      type: 'string',
      required: true,
      placeholder: 'stakeholder-uuid',
      description: 'The stakeholder ID',
      displayOptions: {
        show: {
          action: ['getStakeholder', 'updateStakeholder', 'deleteStakeholder'],
        },
      },
    },
    // Stakeholder Type ID field
    {
      key: 'stakeholderTypeId',
      label: 'Stakeholder Type ID',
      type: 'string',
      required: true,
      placeholder: 'stakeholder-type-uuid',
      description: 'The stakeholder type ID',
      displayOptions: {
        show: {
          action: ['getStakeholderType', 'updateStakeholderType', 'deleteStakeholderType'],
        },
      },
    },
    // Task Type ID field
    {
      key: 'taskTypeId',
      label: 'Task Type ID',
      type: 'string',
      required: true,
      placeholder: 'task-type-uuid',
      description: 'The task type ID',
      displayOptions: {
        show: {
          action: ['getTaskType', 'updateTaskType', 'deleteTaskType'],
        },
      },
    },
    // Comment ID field
    {
      key: 'commentId',
      label: 'Comment ID',
      type: 'string',
      required: true,
      placeholder: 'comment-uuid',
      description: 'The comment ID',
      displayOptions: {
        show: {
          action: ['updateComment', 'deleteComment'],
        },
      },
    },
    // Team ID field
    {
      key: 'teamId',
      label: 'Team ID',
      type: 'string',
      required: true,
      placeholder: 'team-uuid',
      description: 'The team ID',
      displayOptions: {
        show: {
          action: ['getTeam', 'updateTeam', 'deleteTeam'],
        },
      },
    },
    // Board ID filter (optional for getTasks)
    {
      key: 'filterBoardId',
      label: 'Filter by Board ID',
      type: 'string',
      required: false,
      placeholder: 'board-uuid (optional)',
      description: 'Filter tasks by board ID (leave empty to get all tasks)',
      displayOptions: {
        show: {
          action: ['getTasks'],
        },
      },
    },
    // Column ID filter (optional for getTasks)
    {
      key: 'filterColumnId',
      label: 'Filter by Column ID',
      type: 'string',
      required: false,
      placeholder: 'column-uuid (optional)',
      description: 'Filter tasks by column ID (leave empty to get all)',
      displayOptions: {
        show: {
          action: ['getTasks'],
        },
      },
    },
    // Task ID field
    {
      key: 'taskId',
      label: 'Task ID',
      type: 'string',
      required: true,
      placeholder: 'task-uuid or {{previousNode.taskId}}',
      description: 'The task ID (supports variable interpolation)',
      displayOptions: {
        show: {
          action: [
            'getTask',
            'updateTask',
            'moveTask',
            'removeFromColumn',
            'removeFromBoard',
            'assignToBoard',
            'deleteTask',
            'getComments',
            'addComment',
            'updateComment',
            'deleteComment',
          ],
        },
      },
    },
    // Column ID field
    {
      key: 'columnId',
      label: 'Column ID',
      type: 'string',
      required: true,
      placeholder: 'column-uuid',
      description: 'The column ID',
      displayOptions: {
        show: {
          action: ['updateColumn', 'deleteColumn', 'reorderColumn'],
        },
      },
    },
    // Target Column ID field (for move task)
    {
      key: 'targetColumnId',
      label: 'Target Column ID',
      type: 'string',
      required: true,
      placeholder: 'target-column-uuid',
      description: 'The column to move the task to',
      displayOptions: {
        show: {
          action: ['moveTask'],
        },
      },
    },
    // Tag ID field
    {
      key: 'tagId',
      label: 'Tag ID',
      type: 'string',
      required: true,
      placeholder: 'tag-uuid',
      description: 'The tag ID',
      displayOptions: {
        show: {
          action: ['getTag', 'updateTag', 'deleteTag'],
        },
      },
    },
    // Single unified body field for all actions that need a request body
    {
      key: 'body',
      label: 'Request Body',
      type: 'json',
      required: true,
      default: JSON.stringify({
        name: 'Example Name',
        description: 'Example description',
      }, null, 2),
      description: 'JSON body for the request. See API documentation for required fields per action.',
      displayOptions: {
        show: {
          action: [
            'createBoard',
            'updateBoard',
            'addBoardMember',
            'updateBoardMember',
            'createColumn',
            'updateColumn',
            'reorderColumn',
            'createTask',
            'updateTask',
            'moveTask',
            'assignToBoard',
            'createTaskType',
            'updateTaskType',
            'addComment',
            'updateComment',
            'createStakeholder',
            'updateStakeholder',
            'createStakeholderType',
            'updateStakeholderType',
            'createTag',
            'updateTag',
            'createTeam',
            'updateTeam',
          ],
        },
      },
    },
  ],
};

export default schema;
